import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";

const PANELS = [
  {
    image: "/assets/mockup-hero.jpg",
    kicker: "Drop 01",
    title: "Fierce Attire",
    caption: "Heavyweight layers built for the city jungle.",
  },
  {
    image: "/assets/mockup-4.jpg",
    kicker: "Graphic Tees",
    title: "Roar Loud",
    caption: "Oversized fits with high-density tiger prints.",
  },
  {
    image: "/assets/mockup-6.jpg",
    kicker: "Heavyweight Hoodies",
    title: "Built Warm",
    caption: "240 GSM brushed fleece. Zero compromise.",
  },
  {
    image: "/assets/mockup-5.jpg",
    kicker: "Street Ready",
    title: "Own It",
    caption: "Tonal graphics for everyday prowling.",
  },
  {
    image: "/assets/mockup-3.jpg",
    kicker: "The Pack",
    title: "Join Us",
    caption: "Printed on demand. Worn by the bold.",
  },
];

const LookbookPanel = ({ panel, index }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section ref={ref} data-testid={`lookbook-panel-${index}`} className="relative h-[85vh] overflow-hidden">
      <motion.img
        src={panel.image}
        alt={panel.title}
        style={{ y }}
        className="absolute inset-0 h-[124%] w-full object-cover"
      />
      <div className="absolute inset-0 bg-ink/40" />
      <div className="absolute inset-0 flex items-end">
        <div className="mx-auto w-full max-w-7xl px-4 pb-16 sm:px-8 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 48 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-120px" }}
            transition={{ duration: 0.7 }}
          >
            <p className="mb-3 inline-block bg-tiger px-3 py-1 font-heading text-[10px] font-bold uppercase tracking-[0.3em] text-black">
              {panel.kicker}
            </p>
            <h2 className="font-display text-5xl uppercase leading-none text-white sm:text-7xl lg:text-8xl">
              {panel.title}
            </h2>
            <p className="mt-4 max-w-md font-body text-sm text-zinc-200 sm:text-base">{panel.caption}</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default function LookbookPage() {
  return (
    <div className="min-h-screen bg-ink font-body text-white" data-testid="lookbook-page">
      <SiteHeader />
      <section className="relative flex h-[60vh] items-center justify-center overflow-hidden border-b border-zinc-800" data-testid="lookbook-intro">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="relative text-center"
        >
          <div className="mx-auto mb-8 h-1 w-16 bg-tiger" />
          <p className="mb-4 font-body text-xs font-medium uppercase tracking-[0.3em] text-tiger">Tiger Clothing — Drop 01</p>
          <h1 className="font-display text-6xl uppercase leading-none sm:text-8xl">
            The <span className="text-tiger">Lookbook</span>
          </h1>
          <p className="mt-6 font-body text-sm text-zinc-400">Scroll to prowl the collection.</p>
        </motion.div>
      </section>

      {PANELS.map((panel, i) => (
        <LookbookPanel key={panel.title} panel={panel} index={i} />
      ))}

      <section className="flex flex-col items-center gap-6 py-24 text-center" data-testid="lookbook-cta">
        <h2 className="font-display text-4xl uppercase sm:text-6xl">
          Ready To <span className="text-tiger">Roar?</span>
        </h2>
        <Link
          to="/gb/en/home.html#shop"
          data-testid="lookbook-shop-btn"
          className="group flex items-center gap-2 bg-tiger px-10 py-4 font-heading text-sm font-bold uppercase tracking-widest text-black transition-all hover:-translate-y-1 hover:bg-tiger-hover"
        >
          Shop Drop 01 <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
        </Link>
      </section>
      <SiteFooter />
    </div>
  );
}
